import React, { useEffect, useState } from 'react';
import {
  regions as getRegions,
  provinces as getProvinces,
  cities as getCities,
  barangays as getBarangays,
} from 'select-philippines-address';

/**
 * Cascading Philippine address selector: Province -> City/Municipality -> Barangay.
 * Emits the chosen NAMES (matching what the backend stores) via onChange.
 *
 * Data comes from the bundled PSGC dataset (`select-philippines-address`), so it
 * works without any external API call.
 *
 * Controlled component:
 *   <PHAddressSelect
 *     province={province} city={city} barangay={barangay}
 *     onChange={({ province, city, barangay }) => setForm(...)} />
 */
const SELECT_CLS =
  'w-full h-11 px-4 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition disabled:bg-gray-50 disabled:text-gray-400';

const norm = (s) => (s || '').trim().toLowerCase();

export const PHAddressSelect = ({
  province = '',
  city = '',
  barangay = '',
  onChange,
  idPrefix = 'addr',
  className = '',
}) => {
  const [provinceList, setProvinceList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [brgyList, setBrgyList] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(true);

  // Load every province across all regions once (flattened, alphabetical).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const regs = await getRegions();
        const all = [];
        for (const r of regs) {
          // eslint-disable-next-line no-await-in-loop
          const provs = await getProvinces(r.region_code);
          all.push(...provs);
        }
        all.sort((a, b) => a.province_name.localeCompare(b.province_name));
        if (active) setProvinceList(all);
      } catch {
        /* noop */
      } finally {
        if (active) setLoadingProvinces(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Load cities whenever the selected province (or the province list) changes.
  useEffect(() => {
    const p = provinceList.find((x) => norm(x.province_name) === norm(province));
    if (!p) {
      setCityList([]);
      return undefined;
    }
    let active = true;
    getCities(p.province_code)
      .then((cs) => {
        cs.sort((a, b) => a.city_name.localeCompare(b.city_name));
        if (active) setCityList(cs);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [province, provinceList]);

  // Load barangays whenever the selected city (or the city list) changes.
  useEffect(() => {
    const c = cityList.find((x) => norm(x.city_name) === norm(city));
    if (!c) {
      setBrgyList([]);
      return undefined;
    }
    let active = true;
    getBarangays(c.city_code)
      .then((bs) => {
        bs.sort((a, b) => a.brgy_name.localeCompare(b.brgy_name));
        if (active) setBrgyList(bs);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [city, cityList]);

  const emit = (next) => onChange?.({ province, city, barangay, ...next });

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Province */}
      <select
        value={province}
        onChange={(e) => emit({ province: e.target.value, city: '', barangay: '' })}
        className={SELECT_CLS}
        disabled={loadingProvinces}
        data-testid={`${idPrefix}-province-select`}
      >
        <option value="">{loadingProvinces ? 'Loading provinces…' : 'Select Province'}</option>
        {provinceList.map((p, i) => (
          <option key={`${p.province_code}-${i}`} value={p.province_name}>
            {p.province_name}
          </option>
        ))}
      </select>

      {/* City / Municipality */}
      <select
        value={city}
        onChange={(e) => emit({ city: e.target.value, barangay: '' })}
        className={SELECT_CLS}
        disabled={!province || cityList.length === 0}
        data-testid={`${idPrefix}-city-select`}
      >
        <option value="">{province ? 'Select City / Municipality' : 'Select province first'}</option>
        {cityList.map((c, i) => (
          <option key={`${c.city_code}-${i}`} value={c.city_name}>
            {c.city_name}
          </option>
        ))}
      </select>

      {/* Barangay */}
      <select
        value={barangay}
        onChange={(e) => emit({ barangay: e.target.value })}
        className={SELECT_CLS}
        disabled={!city || brgyList.length === 0}
        data-testid={`${idPrefix}-barangay-select`}
      >
        <option value="">{city ? 'Select Barangay' : 'Select city first'}</option>
        {brgyList.map((b, i) => (
          <option key={`${b.brgy_code}-${i}`} value={b.brgy_name}>
            {b.brgy_name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PHAddressSelect;
